import { z } from 'zod';
import * as dt from './data.types.js';
import { containerSlotScanJobProgressSchema } from './container-slot-scan.schemas.js';
import { batchWaferTransferJobProgressSchema } from './batch-wafer-transfer.schemas.js';
import { workflowContainerFileMetadataSchema } from './container-workflow-media.js';
/**
 * Same API rules as waferFingerprintSchema (trim, max 250, min 1 when set).
 * Defined here — not imported from schemas.ts — to avoid circular init via
 * `export * from './job-workflow.schemas.js'` at the end of schemas.ts.
 */
const jobWorkflowFingerprintSchema = z.string().trim().min(1).max(250);
const nullableJobWorkflowFingerprintSchema = z.preprocess((val) => (val === '' || val === undefined ? null : val), jobWorkflowFingerprintSchema.nullable().optional());
const BaseResponseSchema = z.object({
    success: z.boolean(),
    message: z.string().optional(),
});
const integerIdSchema = z.coerce.number().int().positive();
const containerSlotSchema = z.coerce.number().int().min(1).max(25);
export const jobSuccessTierSchema = z.nativeEnum(dt.JOB_SUCCESS_TIER);
export const unifiedJobTypeSchema = z.nativeEnum(dt.UNIFIED_JOB_TYPE);
export const importedJobRefSchema = z
    .object({
    jobId: z.string().min(1).max(120),
    jobType: unifiedJobTypeSchema,
})
    .strict();
export const entityNoteEntityTypeSchema = z.nativeEnum(dt.ENTITY_NOTE_ENTITY_TYPE);
export const containerWorkflowTypeSchema = z.nativeEnum(dt.CONTAINER_WORKFLOW_TYPE);
export const containerWorkflowDraftStatusSchema = z.nativeEnum(dt.CONTAINER_WORKFLOW_DRAFT_STATUS);
export const fingerprintOverrideBodySchema = z.object({
    userFingerprintOverride: jobWorkflowFingerprintSchema.nullable(),
});
export const fingerprintOverrideSlotResultSchema = z.object({
    slotId: z.string().min(1),
    slotNumber: containerSlotSchema,
    userFingerprintOverride: jobWorkflowFingerprintSchema.nullable(),
    effectiveFingerprint: jobWorkflowFingerprintSchema.nullable(),
});
export const fingerprintOverrideResponseSchema = BaseResponseSchema.extend({
    data: fingerprintOverrideSlotResultSchema,
});
/** Route `:slotNumber` is the FOUP slot index (1–25), not the `container_slot_scan_job_slots.id` uuid. */
export const containerSlotScanSlotOverrideParamSchema = z.object({
    jobId: z.string().min(1),
    slotNumber: containerSlotSchema,
});
/** @deprecated Alias — param is slot number, not row uuid. */
export const containerSlotScanSlotIdParamSchema = containerSlotScanSlotOverrideParamSchema;
const emptyQueryValueToUndefined = (val) => val === '' || val === null ? undefined : val;
export const tenantJobsListQuerySchema = z.object({
    type: z.preprocess(emptyQueryValueToUndefined, z.enum(['container_slot_scan', 'batch_wafer_transfer', 'all']).default('all')),
    status: z.preprocess(emptyQueryValueToUndefined, z.enum(['fully_successful', 'partial', 'failed', 'in_progress', 'all']).default('all')),
    view: z.enum(['mine', 'all']).default('mine'),
    efemClientId: z.coerce.number().int().positive().optional(),
    containerId: z.coerce.number().int().positive().optional(),
    dateFrom: z.string().datetime().optional(),
    dateTo: z.string().datetime().optional(),
    page: z.coerce.number().int().min(1).default(1),
    limit: z.coerce.number().int().min(1).max(100).default(20),
});
export const tenantJobDetailQuerySchema = z.object({
    jobType: unifiedJobTypeSchema.optional(),
});
export const tenantJobEventsQuerySchema = z.object({
    jobType: unifiedJobTypeSchema.optional(),
});
export const tenantJobProgressQuerySchema = tenantJobDetailQuerySchema;
const unifiedJobProgressBaseSchema = z.object({
    jobId: z.string(),
    phase: z.string(),
    isTerminal: z.boolean(),
    completedItems: z.number().int().min(0),
    totalItems: z.number().int().min(0),
    failedItems: z.number().int().min(0),
});
export const unifiedJobProgressSchema = z.discriminatedUnion('jobType', [
    unifiedJobProgressBaseSchema.extend({
        jobType: z.literal(dt.UNIFIED_JOB_TYPE.CONTAINER_SLOT_SCAN),
        progress: containerSlotScanJobProgressSchema,
    }),
    unifiedJobProgressBaseSchema.extend({
        jobType: z.literal(dt.UNIFIED_JOB_TYPE.BATCH_WAFER_TRANSFER),
        progress: batchWaferTransferJobProgressSchema,
    }),
]);
export const tenantJobProgressResponseSchema = BaseResponseSchema.extend({
    data: unifiedJobProgressSchema,
});
export const tenantJobsActiveCheckQuerySchema = z.object({
    containerIds: z
        .string()
        .transform((s) => s
        .split(',')
        .map((x) => Number.parseInt(x.trim(), 10))
        .filter((n) => Number.isFinite(n) && n > 0))
        .optional(),
});
export const unifiedJobListItemSchema = z.object({
    jobId: z.string(),
    jobType: unifiedJobTypeSchema,
    successTier: jobSuccessTierSchema,
    phase: z.string(),
    startedAt: z.string().nullable(),
    finishedAt: z.string().nullable(),
    efemClientId: z.number().int(),
    efemName: z.string().nullable().optional(),
    efemSerial: z.string().nullable().optional(),
    containerId: z.number().int().positive().nullable().optional(),
    containerIds: z.array(z.number().int().positive()).default([]),
    initiatedByUserId: z.number().int().nullable().optional(),
    initiatedByEmail: z.string().nullable().optional(),
});
export const tenantJobsListResponseSchema = BaseResponseSchema.extend({
    data: z.object({
        items: z.array(unifiedJobListItemSchema),
        total: z.number().int(),
        page: z.number().int(),
        limit: z.number().int(),
    }),
});
export const tenantJobsCreatableTypesResponseSchema = BaseResponseSchema.extend({
    data: z.object({
        creatableTypes: z.array(z.enum(['container_slot_scan', 'batch_wafer_transfer'])),
    }),
});
export const tenantJobsActiveListQuerySchema = z.object({
    view: z.enum(['mine', 'all']).default('mine'),
    type: z.preprocess(emptyQueryValueToUndefined, z.enum(['container_slot_scan', 'batch_wafer_transfer', 'all']).default('all')),
    limit: z.coerce.number().int().min(1).max(100).default(20),
});
export const unifiedActiveJobSchema = z.object({
    jobId: z.string(),
    jobType: unifiedJobTypeSchema,
    phase: z.string(),
    startedAt: z.string().nullable().optional(),
    finishedAt: z.string().nullable().optional(),
    efemClientId: z.number().int(),
    efemName: z.string().nullable().optional(),
    containerId: z.number().int().positive().nullable().optional(),
    containerIds: z.array(z.number().int().positive()).default([]),
    initiatedByUserId: z.number().int().nullable().optional(),
    initiatedByEmail: z.string().nullable().optional(),
    gatewaySerialNumber: z.string().nullable().optional(),
    loadPortId: z.number().int().positive().nullable().optional(),
    totalItems: z.number().int().min(0),
    completedItems: z.number().int().min(0),
    updatedAt: z.string(),
});
export const tenantJobsActiveListResponseSchema = BaseResponseSchema.extend({
    data: z.object({
        items: z.array(unifiedActiveJobSchema),
        total: z.number().int(),
    }),
});
export const tenantJobsStuckListQuerySchema = z.object({
    type: z.preprocess(emptyQueryValueToUndefined, z.enum(['container_slot_scan', 'batch_wafer_transfer', 'all']).default('all')),
});
export const unifiedStuckJobSchema = z.object({
    jobId: z.string(),
    jobType: unifiedJobTypeSchema,
    tenantId: z.string().uuid(),
    phase: z.string(),
    stuckReason: z.string(),
    lastActivityAt: z.string().nullable(),
    containerId: z.number().int().positive().nullable().optional(),
    containerIds: z.array(z.number().int().positive()).default([]),
    efemClientId: z.number().int().optional(),
    updatedAt: z.string(),
});
export const tenantJobsStuckListResponseSchema = BaseResponseSchema.extend({
    data: z.object({
        items: z.array(unifiedStuckJobSchema),
        thresholds: z.record(z.string(), z.number().int().positive()).optional(),
    }),
});
export const tenantJobAbortBodySchema = z.object({
    reason: z.string().max(500).trim().optional(),
});
export const tenantJobAbortQuerySchema = z.object({
    jobType: z.enum(['container_slot_scan', 'batch_wafer_transfer']),
});
export const providerJobsOpsQuerySchema = z.object({
    jobType: z.preprocess(emptyQueryValueToUndefined, z.enum(['container_slot_scan', 'batch_wafer_transfer', 'all']).default('all')),
});
export const efemIdleReconcileSweepResultSchema = z.object({
    jobsScanned: z.number().int(),
    jobsBecameTerminal: z.number().int(),
});
export const efemRetentionTypeResultSchema = z.object({
    timedOutJobs: z.number().int(),
    softDeletedJobs: z.number().int(),
    idleReconcile: efemIdleReconcileSweepResultSchema,
});
export const efemRetentionRunResultSchema = z.object({
    timedOutJobs: z.number().int(),
    softDeletedJobs: z.number().int(),
    idleJobsScanned: z.number().int(),
    idleJobsBecameTerminal: z.number().int(),
    byType: z
        .record(z.string(), efemRetentionTypeResultSchema)
        .optional(),
});
export const providerJobsRetentionRunResponseSchema = BaseResponseSchema.extend({
    data: efemRetentionRunResultSchema,
});
export const jobsActiveCheckResponseSchema = BaseResponseSchema.extend({
    data: z.object({
        blocking: z.array(z.object({
            containerId: z.number().int(),
            jobId: z.string(),
            jobType: unifiedJobTypeSchema,
            phase: z.string(),
            startedAt: z.string().nullable().optional(),
            initiatedByUserId: z.number().int().nullable().optional(),
            initiatedByEmail: z.string().nullable().optional(),
            initiatedByName: z.string().nullable().optional(),
            progressText: z.string().optional(),
        })),
    }),
});
export const fingerprintMergeRuleSchema = z.enum(['replace_all', 'missing_only', 'none']);
export const imageMergeRuleSchema = z.enum(['append', 'replace', 'none']);
export const jobImportPreviewRequestSchema = z.object({
    targetContainerId: integerIdSchema.optional(),
    importedJobRefs: z.array(importedJobRefSchema).min(1).max(10),
    fingerprintMergeRule: fingerprintMergeRuleSchema.default('replace_all'),
    imageMergeRule: imageMergeRuleSchema.default('append'),
    view: z.enum(['mine', 'all']).default('mine'),
});
export const jobImportPreviewResolvedSlotSchema = z.object({
    slotNumber: containerSlotSchema,
    effectiveFingerprint: z.string().nullable(),
    imageObjectKeys: z.array(z.string()),
    sourceJobId: z.string(),
    warnings: z.array(z.string()),
});
export const jobImportPreviewConflictSchema = z.object({
    slotNumber: containerSlotSchema,
    candidates: z.array(z.object({
        jobId: z.string(),
        effectiveFingerprint: z.string().nullable(),
        tier: z.enum(['full', 'partial']),
    })),
});
export const jobImportPreviewResponseSchema = BaseResponseSchema.extend({
    data: z.object({
        resolvedSlots: z.array(jobImportPreviewResolvedSlotSchema),
        conflicts: z.array(jobImportPreviewConflictSchema),
        ignoredJobRefs: z.array(importedJobRefSchema).optional(),
    }),
});
export const workflowDraftCompleteBodySchema = z.object({
    stepData: z.record(z.unknown()).optional(),
});
export const workflowCompleteResponseSchema = BaseResponseSchema.extend({
    data: z.object({
        workflowDraftId: z.string().uuid(),
        workflowType: containerWorkflowTypeSchema,
        containerId: integerIdSchema,
        auditRunId: integerIdSchema.optional(),
        auditRecordCount: z.number().int().optional(),
        conflictCount: z.number().int().optional(),
        openConflictCount: z.number().int().optional(),
    }),
    activityLogWarning: z.string().optional(),
});
export const containerWorkflowActivityDetailsSchema = z
    .object({
    workflowDraftId: z.string().uuid(),
    workflowType: containerWorkflowTypeSchema,
    containerId: integerIdSchema,
    importedJobRefs: z.array(importedJobRefSchema),
    slotCount: z.number().int().optional(),
    auditRunId: integerIdSchema.optional(),
    auditRecordCount: z.number().int().optional(),
    conflictCount: z.number().int().optional(),
})
    .strict();
export const entityNoteCreateBodySchema = z.object({
    entityType: entityNoteEntityTypeSchema,
    entityId: integerIdSchema,
    noteText: z.string().max(10000).optional().nullable(),
});
export const entityNoteUpdateBodySchema = z.object({
    noteText: z.string().max(10000).optional().nullable(),
});
export const entityNoteListQuerySchema = z.object({
    entityType: entityNoteEntityTypeSchema,
    entityId: integerIdSchema,
});
export const entityNoteAttachmentSchema = z.object({
    objectKey: z.string().min(1).max(250).trim(),
    originalName: z.string().min(1).max(250).trim(),
    mimeType: z.string().min(1).max(100).trim(),
    size: z.number().int(),
    uploadedAt: z.string().optional(),
    fileCategory: z.enum(['image', 'document']).optional(),
});
export const entityNoteViewSchema = z.object({
    id: z.string().uuid(),
    entityType: entityNoteEntityTypeSchema,
    entityId: integerIdSchema,
    noteText: z.string().nullable(),
    createdBy: integerIdSchema,
    createdByFirstName: z.string().nullable().optional(),
    createdByLastName: z.string().nullable().optional(),
    createdAt: z.string(),
    updatedAt: z.string(),
    attachmentCount: z.number().int().optional(),
    attachments: z.array(entityNoteAttachmentSchema).optional(),
});
export const entityNoteIdParamSchema = z.object({
    id: z.string().uuid(),
});
export const entityNoteListResponseSchema = BaseResponseSchema.extend({
    data: z.object({
        notes: z.array(entityNoteViewSchema),
    }),
});
export const entityNoteResponseSchema = BaseResponseSchema.extend({
    data: entityNoteViewSchema,
});
export const entityNoteAttachmentsBodySchema = z.object({
    objectKeys: z.array(z.string().min(1)).min(1),
    fileCategory: z.nativeEnum(dt.FILE_CATEGORY),
});
export const workflowDraftListQuerySchema = z.object({
    status: containerWorkflowDraftStatusSchema.default(dt.CONTAINER_WORKFLOW_DRAFT_STATUS.DRAFT),
    view: z.enum(['mine', 'all']).default('mine'),
});
export const workflowDraftCreateBodySchema = z.object({
    workflowType: containerWorkflowTypeSchema,
    targetContainerId: integerIdSchema.optional(),
    currentStep: z.number().int().min(0).default(0),
    importedJobRefs: z.array(importedJobRefSchema).max(10).default([]),
    stepData: z.record(z.unknown()).default({}),
});
export const workflowDraftUpdateBodySchema = z.object({
    status: containerWorkflowDraftStatusSchema.optional(),
    currentStep: z.number().int().min(0).optional(),
    targetContainerId: integerIdSchema.nullable().optional(),
    importedJobRefs: z.array(importedJobRefSchema).max(10).optional(),
    stepData: z.record(z.unknown()).optional(),
});
export const workflowDraftViewSchema = z.object({
    id: z.string().uuid(),
    workflowType: containerWorkflowTypeSchema,
    status: containerWorkflowDraftStatusSchema,
    currentStep: z.number().int(),
    createdBy: integerIdSchema,
    targetContainerId: integerIdSchema.nullable().optional(),
    importedJobRefs: z.array(importedJobRefSchema),
    stepData: z.record(z.unknown()),
    completedAt: z.string().nullable().optional(),
    expiresAt: z.string(),
    createdAt: z.string(),
    updatedAt: z.string(),
});
export const workflowDraftIdParamSchema = z.object({
    id: z.string().uuid(),
});
export const workflowDraftListResponseSchema = BaseResponseSchema.extend({
    data: z.object({
        drafts: z.array(workflowDraftViewSchema),
    }),
});
export const workflowDraftResponseSchema = BaseResponseSchema.extend({
    data: workflowDraftViewSchema,
});
export const tenantJobDetailResponseSchema = BaseResponseSchema.extend({
    data: z.unknown(),
});
export const tenantJobsActiveCheckClientQuerySchema = z.object({
    containerIds: z.string().optional(),
    efemClientId: z.coerce.number().int().positive().optional(),
    gatewaySerialNumber: z.string().optional(),
    loadPortIds: z.string().optional(),
});
export const slotDraftScanImageSchema = z
    .object({
    objectKey: z.string(),
    ocrResult: z.string().nullable().optional(),
    confidence: z.number().min(0).max(1).nullable().optional(),
})
    .strict();
export const slotDraftRowSchema = z
    .object({
    slotNumber: containerSlotSchema,
    fingerprint: nullableJobWorkflowFingerprintSchema,
    effectiveFingerprint: nullableJobWorkflowFingerprintSchema,
    layerName: z.string().optional(),
    type: z.string().optional(),
    size: z.string().optional(),
    material: z.string().optional(),
    thickness: z.string().optional(),
    lotId: z.string().optional(),
    ownerContactVersionId: z.string().min(1).optional(),
    imageObjectKeys: z.array(z.string()).optional(),
    fingerprintImageObjectKeys: z.array(z.string()).optional(),
    /** Scan job images with optional per-frame OCR metadata. */
    scanImages: z.array(slotDraftScanImageSchema).optional(),
    /** Non-image slot attachments (documents, etc.). */
    fileObjectKeys: z.array(z.string()).optional(),
    waferId: integerIdSchema.optional(),
    inlineNotes: z.string().optional(),
    changed: z.boolean().optional(),
    isEmpty: z.boolean().optional(),
})
    .strict();
export const workflowAuditSlotSideDetailSchema = z
    .object({
    fingerprint: z.string().nullable(),
    /** OCR-detected value before user override (job side only). */
    ocrFingerprint: z.string().nullable().optional(),
    /** True when job slot fingerprint was set via userFingerprintOverride. */
    fingerprintUserOverridden: z.boolean().optional(),
    layerName: z.string().optional(),
    waferId: integerIdSchema.optional(),
    imageObjectKeys: z.array(z.string()).optional(),
    fingerprintImageObjectKeys: z.array(z.string()).optional(),
    scanImages: z.array(slotDraftScanImageSchema).optional(),
    sourceJobId: z.string().optional(),
    warnings: z.array(z.string()).optional(),
})
    .strict();
export const workflowAuditComparisonSlotSchema = z
    .object({
    slotNumber: containerSlotSchema,
    dbFingerprint: z.string().nullable(),
    detectedFingerprint: z.string().nullable(),
    matchStatus: z.enum(['match', 'mismatch', 'ghost', 'missing', 'not_compared']),
    dbDetail: workflowAuditSlotSideDetailSchema.optional(),
    jobDetail: workflowAuditSlotSideDetailSchema.optional(),
})
    .strict();
export const workflowAuditComparisonSnapshotSchema = z
    .object({
    schemaVersion: z.literal(1),
    containerId: integerIdSchema,
    comparedAt: z.string(),
    jobRefs: z.array(importedJobRefSchema),
    slots: z.array(workflowAuditComparisonSlotSchema),
})
    .strict();
export const workflowDraftNoteSchema = z
    .object({
    id: z.string().min(1),
    noteText: z.string().max(10000),
})
    .strict();
const containerWorkflowDateFieldSchema = z.union([
    z.string().min(1),
    z.coerce.date(),
]);
export const containerWorkflowUpdateFieldsSchema = z
    .object({
    containerBarcode: z.string().max(64).nullable().optional(),
    beaconId: z.string().optional(),
    ownerContactVersionId: z.string().min(1).optional(),
    customerId: integerIdSchema.optional(),
    companyAlias: z.string().min(1).max(50).trim().optional(),
    groupIds: z.array(integerIdSchema).min(1).optional(),
    status: z.nativeEnum(dt.CONTAINER_STATUS).optional(),
    receivedDate: containerWorkflowDateFieldSchema.optional(),
    shippedDate: containerWorkflowDateFieldSchema.optional(),
})
    .strict();
export const CONTAINER_WORKFLOW_STEP_DATA_SCHEMA_VERSION = 2;
export const containerWorkflowStepDataSchema = z
    .object({
    schemaVersion: z
        .literal(CONTAINER_WORKFLOW_STEP_DATA_SCHEMA_VERSION)
        .default(CONTAINER_WORKFLOW_STEP_DATA_SCHEMA_VERSION),
    importOptions: z
        .object({
        fingerprintMergeRule: z.enum(['replace_all', 'missing_only', 'none']),
        imageMergeRule: z.enum(['append', 'replace', 'none']),
    })
        .strict()
        .optional(),
    containerImageMergeRule: z.enum(['append', 'replace']).optional(),
    slots: z.array(slotDraftRowSchema).max(25).optional(),
    containerFields: z.record(z.unknown()).optional(),
    containerImages: z.array(workflowContainerFileMetadataSchema).max(10).optional(),
    containerFiles: z.array(workflowContainerFileMetadataSchema).max(10).optional(),
    containerDraftNotes: z.array(workflowDraftNoteSchema).max(50).optional(),
    audit: z
        .object({
        selectedSlots: z.array(containerSlotSchema),
        comparisonSnapshot: workflowAuditComparisonSnapshotSchema,
    })
        .strict()
        .optional(),
})
    .strict();
//# sourceMappingURL=job-workflow.schemas.js.map